// App Configuration - Text data, metadata, and dynamic pages
export interface AppConfigData {
  metadata: {
    name: string;
  };
  pages: {
    [pageRoute: string]: {
      name: string;
      subTitle: string;
      agentLabel: string;
      welcomeTitle: string;
      welcomeSubTitle: string;
      searchPlaceholder: string;
      infoHeader: string;
      infoSubHeader: string;
      infoList: Array<{
        title: string;
        list: Array<{
          header: string;
          context: string;
        }>;
      }>;
      trendingSearches: string[];
      recentSearches: string[];
      allowMode?: boolean;
    };
  };
}

// Static Configuration - currently empty
export type StaticConfigData = object;

// Completely Dynamic Configuration Types
export enum SettingType {
  TOGGLE = 'TOGGLE',
  SELECT = 'SELECT',
}

// Generic option for any setting
export interface SettingOption {
  label: string;
  value?: boolean;
}

// Generic setting interface - completely dynamic
export interface DynamicSetting {
  type: SettingType;
  label: string;
  icon?: string;
  defaultValue?: boolean;
  multiple?: boolean;
  data?: Record<string, SettingOption>;
  [key: string]: unknown; // Allow any additional properties
}

// Completely dynamic settings tab - any key can be anything
export interface DynamicSettingsTab {
  visible?: boolean;
  label?: string;
  [settingName: string]: DynamicSetting | boolean | string | unknown; // Any setting name with any value
}

// Completely dynamic settings panel - any key can be anything
export interface DynamicSettingsPanel {
  [settingsTabName: string]: DynamicSettingsTab; // Any tab name with any structure
}

// Completely dynamic config data - any key can be anything
export interface DynamicConfigData {
  [key: string]: unknown; // Allow any key with any value
  settingsPanel?: DynamicSettingsPanel;
}

// Combined Configuration Interface
export interface AppConfig {
  metadata: AppConfigData['metadata'];
  pages: AppConfigData['pages'];
  layout?: StaticConfigData;
  [key: string]: unknown; // Allow any additional properties
}

// Session Storage Format - completely dynamic
// For TOGGLE: {[settingsName]: boolean}
// For SELECT: {[selectName]: boolean}
export interface SettingsValueMap {
  [key: string]: boolean;
}

export interface ConfigContextType {
  config: AppConfig;
  isLoading: boolean;
  error: string | null;
  updateConfig: (updates: Partial<AppConfig>) => void;
  reloadConfig: () => Promise<void>;
  getAppConfig: () => AppConfigData;
  getStaticConfig: () => StaticConfigData;
  getDynamicConfig: () => DynamicConfigData;
  getSettingsValueMap: () => SettingsValueMap;
  getSettingsValueMapWithStorage: () => SettingsValueMap;
  mapStorageToConfig: (storedData: SettingsValueMap) => Record<string, unknown>;
  saveSettingsToStorage: () => void;
  loadSettingsFromStorage: () => SettingsValueMap;
  clearSettingsFromStorage: () => void;
  getVisibleTabs: () => string[];
  getTabSettings: (tabName: string) => Record<string, unknown>;
  getTabLabels: () => Array<{ name: string; label: string }>;
  getTabLabel: (tabName: string) => string;
  getPageConfig: (pageRoute: string) => AppConfigData['pages'][string] | undefined;
  getPageRoutes: () => string[];
  hasPageRoute: (pageRoute: string) => boolean;
  getPageMetadata: (pageRoute: string) => { name: string; subTitle: string } | undefined;
  getPageAgentLabel: (pageRoute: string) => string | undefined;
  getPageInfo: (pageRoute: string) => {
    header: string;
    subHeader: string;
    list: Array<{
      title: string;
      list: Array<{
        header: string;
        context: string;
      }>;
    }>;
  } | undefined;
} 